import Client from "./Client";
import ClientSchool from "./ClientSchool";
import School from "./School";
import Service from "./Service";
import Assignment from "./Assignment";
import { TransmittalStatus } from "./TransmittalStatus";
import Program from "./Program";
import Semester from "./Semester";
import { formatDate } from "@/utils/format-utils";

export default class Transmittal {
  transmittalId?: number | null;
  transmittalReportId?: number | null;
  programId?: number | null;
  program?: Program | null;
  semesterId?: number | null;
  semester?: Semester | null;
  uploadedAt?: Date | null;
  clientId?: number | null;
  client?: Client | null;
  isNewClient?: boolean | null;
  clientSchoolId?: number | null;
  clientSchool?: ClientSchool | null;
  school?: School | null;
  isNewClientSchool?: boolean | null;
  serviceId?: number | null;
  service?: Service | null;
  groupSize?: number | null;
  frequency?: number | null;
  durationMinutes?: number | null;
  serviceStartDate?: Date | null;
  processedAt?: Date | null;
  acceptedAt?: Date | null;
  refusedAt?: Date | null;
  rejectedAt?: Date | null;
  revokedAt?: Date | null;
  fadSubmittedAt?: Date | null;
  errorMessage?: string | null;
  resolvedAt?: Date | null;
  resolutionMessage?: string | null;
  status?: string | null;
  // Transmittal fields to display if error linking to Client/School/Service
  clientOSIS?: string | null;
  clientLastName?: string | null;
  clientFirstName?: string | null;
  schoolDBN?: string | null;
  serviceName?: string | null;
  registeredSchool?: School | null;

  public constructor(params: Transmittal = {} as Transmittal) {
    let {
      transmittalId = 0,
      transmittalReportId = null,
      programId = null,
      program = null,
      semesterId = null,
      semester = null,
      uploadedAt = null,
      clientId = null,
      client = null,
      isNewClient = null,
      clientSchoolId = null,
      clientSchool = null,
      school = null,
      isNewClientSchool = null,
      serviceId = null,
      service = null,
      groupSize = null,
      frequency = null,
      durationMinutes = null,
      serviceStartDate = null,
      processedAt = null,
      acceptedAt = null,
      refusedAt = null,
      rejectedAt = null,
      revokedAt = null,
      fadSubmittedAt = null,
      errorMessage = null,
      resolvedAt = null,
      resolutionMessage = null,
      status = null,
      clientOSIS = null,
      clientLastName = null,
      clientFirstName = null,
      schoolDBN = null,
      serviceName = null,
      registeredSchool = null,
    } = params;
    this.transmittalId = transmittalId;
    this.transmittalReportId = transmittalReportId;
    this.programId = programId;
    this.program = program && new Program(program);
    this.semesterId = semesterId;
    this.semester = semester && new Semester(semester);
    this.uploadedAt = uploadedAt && new Date(uploadedAt);
    this.clientId = clientId;
    this.client = client && new Client(client);
    this.isNewClient = isNewClient;
    this.clientSchoolId = clientSchoolId;
    this.clientSchool = clientSchool && new ClientSchool(clientSchool);
    this.school = school && new School(school);
    this.isNewClientSchool = isNewClientSchool;
    this.serviceId = serviceId;
    this.service = service && new Service(service);
    this.groupSize = groupSize;
    this.frequency = frequency;
    this.durationMinutes = durationMinutes;
    this.serviceStartDate = serviceStartDate;
    this.processedAt = processedAt && new Date(processedAt);
    this.acceptedAt = acceptedAt && new Date(acceptedAt);
    this.refusedAt = refusedAt && new Date(refusedAt);
    this.rejectedAt = rejectedAt && new Date(rejectedAt);
    this.revokedAt = revokedAt && new Date(revokedAt);
    this.fadSubmittedAt = fadSubmittedAt && new Date(fadSubmittedAt);
    this.errorMessage = errorMessage;
    this.resolvedAt = resolvedAt;
    this.resolutionMessage = resolutionMessage;
    this.clientOSIS = clientOSIS;
    this.clientLastName = clientLastName;
    this.clientFirstName = clientFirstName;
    this.schoolDBN = schoolDBN;
    this.serviceName = serviceName;
    this.status = status;
    this.registeredSchool = registeredSchool && new School(registeredSchool);
  }

  get programCode() {
    return this.program?.code;
  }

  get semesterCode() {
    return this.semester?.semesterCode;
  }

  // display DOE Transmittal fields if error linking to Client/School/Service
  get clientName() {
    return (
      this.client?.fullName || `${this.clientFirstName} ${this.clientLastName}`
    ).toUpperCase();
  }

  get clientOsis() {
    return this.client?.formattedOsis || this.clientOSIS;
  }

  get clientDob() {
    return this.client?.formattedDob;
  }

  get districtId() {
    return this.client?.districtId;
  }

  get schoolName() {
    if (!Boolean(this.school?.schoolName)) return this.schoolDBN;
    return Boolean(this.schoolDBN)
      ? `${this.school?.schoolName} (${this.schoolDBN})`
      : this.school?.schoolName;
  }
  get registeredSchoolName() {
    return Boolean(this.registeredSchool?.dbn)
      ? `${this.registeredSchool?.schoolName} (${this.registeredSchool?.dbn})`
      : this.registeredSchool?.schoolName;
  }
  get serviceCode() {
    return this.service?.code || this.serviceName;
  }

  get mandate() {
    if (
      Boolean(this.frequency) &&
      Boolean(this.durationMinutes) &&
      Boolean(this.groupSize)
    )
      return (
        this.frequency + " x " + this.durationMinutes + " : " + this.groupSize
      );
    return "";
  }

  get formattedServiceStartDate() {
    return formatDate(this.serviceStartDate);
  }

  get isError() {
    return this.status == TransmittalStatus.ERROR;
  }

  get statusColor() {
    switch (this.status) {
      case TransmittalStatus.ERROR:
        return "warning";
      case TransmittalStatus.ACCEPTED:
        return "accent";
      case TransmittalStatus.REFUSED:
      case TransmittalStatus.REJECTED:
      case TransmittalStatus.REVOKED:
        return "secondary";
      case TransmittalStatus.RESOLVED:
        return "info";
      case TransmittalStatus.FAD_SUBMITTED:
        return "primary";
      case TransmittalStatus.PENDING:
      default:
        return "#808080";
    }
  }

  get message() {
    if (this.status == TransmittalStatus.RESOLVED)
      return this.resolutionMessage;
    else if (Boolean(this.errorMessage)) return this.errorMessage;
    return "";
  }
}
